import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { userApi } from "../../api/user";
import { useAuth } from "../../context/AuthContext";

export default function UserHeirarchy() {
    const { tokens } = useAuth();
    const [userHeirarchy, setUserHeirarchy] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { userId } = useParams();

    useEffect(() => {
        const fetchUserHeirarchy = async () => {
            setLoading(true);
            try {
                const { status, data } = await userApi.getUserHeirarchy(tokens.access.token, userId);
                if (status === 200) {
                    setUserHeirarchy(data);
                } else {
                    setError("Failed to fetch user hierarchy");
                    toast.error("Failed to fetch user hierarchy");
                }
            } catch (err) {
                setError("An error occurred while fetching user hierarchy");
                toast.error("An error occurred while fetching user hierarchy");
            } finally {
                setLoading(false);
            }
        }
        fetchUserHeirarchy();
    }, [userId, tokens.access.token]);

    // Function to get role name based on roleId
    const getRoleName = (roleId) => {
        const roles = {
            1: "Admin",
            2: "Incorporator",
            3: "Sub Operator",
            4: "General Coordinator",
            5: "Coordinator",
            6: "Sub Coordinator",
            7: "Agent",
            8: "Sub Agent",
            9: "Player"
        };
        return roles[roleId] || `Role ${roleId}`;
    };

    // Function to get a background color based on role
    const getRoleColor = (roleId) => {
        const colors = {
            1: "bg-blue-600",
            2: "bg-orange-400",
            3: "bg-yellow-400",
            4: "bg-purple-700",
            5: "bg-green-500",
            6: "bg-pink-400",
            7: "bg-cyan-500",
            8: "bg-amber-500",
            9: "bg-gray-500"
        };
        return colors[roleId] || "bg-gray-400";
    };

    // Function to render skipped roles indicator
    const renderSkippedRoles = (currentRoleId, nextRoleId) => {
        if (nextRoleId - currentRoleId <= 1) return null;
        
        const skippedRoles = [];
        for (let i = currentRoleId + 1; i < nextRoleId; i++) {
            skippedRoles.push(getRoleName(i));
        }
        
        return (
            <div className="flex justify-center my-2">
                <div className="bg-gray-100 rounded-full px-3 py-1 text-xs text-gray-500 border border-gray-200">
                    Skipped: {skippedRoles.join(', ')}
                </div>
            </div>
        );
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <h1 className="text-2xl font-bold mb-5 text-gray-800">User Hierarchy Tree</h1>
            
            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
            ) : error ? (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <strong className="font-bold">Error! </strong>
                    <span className="block sm:inline">{error}</span>
                </div>
            ) : userHeirarchy?.user ? (
                <div className="max-w-3xl mx-auto">
                    {/* Main hierarchy container */}
                    <div className="bg-white rounded-lg shadow-lg p-5 border border-gray-200">
                        {/* Tree visualization */}
                        <div className="relative mb-6">
                            <div className="absolute h-full w-0.5 bg-gray-300 left-6 top-0 z-0"></div>
                            
                            {/* Display all hierarchy entries with the highest role (lowest roleId) first */}
                            {userHeirarchy.user.hierarchy && [...userHeirarchy.user.hierarchy]
                                .sort((a, b) => a.roleId - b.roleId)
                                .map((hierarchyUser, index, array) => {
                                    const nextUser = index < array.length - 1 ? array[index + 1] : userHeirarchy.user.user;
                                    
                                    return (
                                        <div key={hierarchyUser.id} className="relative z-10 mb-4">
                                            <div className="flex items-start">
                                                <div className={`flex-shrink-0 w-12 h-12 rounded-full ${getRoleColor(hierarchyUser.roleId)} flex items-center justify-center text-white font-bold shadow-md`}>
                                                    {hierarchyUser.roleId}
                                                </div>
                                                <div className="ml-4 flex-grow">
                                                    <div className={`rounded-lg shadow-sm border border-gray-200 overflow-hidden`}>
                                                        <div className={`${getRoleColor(hierarchyUser.roleId)} p-3 text-white`}>
                                                            <div className="font-bold">{getRoleName(hierarchyUser.roleId)}</div>
                                                        </div>
                                                        <div className="p-3 bg-gray-50">
                                                            <div className="font-semibold text-lg">{hierarchyUser.name}</div>
                                                            {/* <div className="text-gray-600">{hierarchyUser.mobileNumber}</div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Show skipped roles indicator if needed */}
                                            {renderSkippedRoles(hierarchyUser.roleId, nextUser.roleId)}
                                            
                                            {/* Show connector line */}
                                            <div className="absolute h-6 w-0.5 bg-gray-300 left-6 -bottom-6"></div>
                                        </div>
                                    );
                                })}
                            
                            {/* Display main user (current user) */}
                            <div className="relative z-10">
                                <div className="flex items-start">
                                    <div className={`flex-shrink-0 w-12 h-12 rounded-full ${getRoleColor(userHeirarchy.user.user.roleId)} flex items-center justify-center text-white font-bold shadow-md`}>
                                        {userHeirarchy.user.user.roleId}
                                    </div>
                                    <div className="ml-4 flex-grow">
                                        <div className="rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                            <div className={`${getRoleColor(userHeirarchy.user.user.roleId)} p-3 text-white`}>
                                                <div className="font-bold flex justify-between items-center">
                                                    <span>{getRoleName(userHeirarchy.user.user.roleId)}</span>
                                                    <span className="text-xs bg-white text-gray-700 px-2 py-0.5 rounded-full">Current User</span>
                                                </div>
                                            </div>
                                            <div className="p-3 bg-gray-50">
                                                <div className="font-semibold text-lg">{userHeirarchy.user.user.name}</div>
                                                {/* <div className="text-gray-600">{userHeirarchy.user.user.mobileNumber}</div> */}
                                                {/* <div className="text-xs text-gray-500 mt-1">ID: {userHeirarchy.user.user.id}</div> */}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Role legend */}
                        {/* <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <h3 className="text-sm font-semibold mb-2 text-gray-700">Role Legend</h3>
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                                {Object.entries({1: "Admin", 2: "Incorporator", 3: "Sub Operator", 4: "General Coordinator", 
                                                 5: "Coordinator", 6: "Sub Coordinator", 7: "Agent", 8: "Sub Agent", 9: "Player"})
                                    .map(([id, name]) => (
                                        <div key={id} className="flex items-center">
                                            <div className={`w-4 h-4 rounded-full ${getRoleColor(parseInt(id))} mr-2`}></div>
                                            <span className="text-xs">{id}: {name}</span>
                                        </div>
                                    ))
                                }
                            </div>
                        </div> */}
                    </div>
                </div>
            ) : (
                <div className="flex p-4 mb-4 text-sm text-yellow-800 border border-yellow-300 rounded-lg bg-yellow-50" role="alert">
                    <svg className="flex-shrink-0 inline w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
                    </svg>
                    <div>
                        <span className="font-medium">No hierarchy data available for this user.</span>
                    </div>
                </div>
            )}
        </div>
    );
}
